import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApproveVendorBankChange,
  BulkReverifyResult,
  ConfirmVendorBankChange,
  CreateSupplierBranch,
  CreateSupplierContact,
  CreateSupplierWithBranch,
  CreateVendorBankChange,
  RejectVendorBankChange,
  ReturnVendorBankChange,
  Supplier,
  Supplier360,
  SupplierBranch,
  SupplierChangeLogRow,
  SupplierContact,
  SupplierListRow,
  UpdateSupplier,
  UpdateSupplierBranch,
  VendorBankChange,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// NOTE — RECONCILED with the real Plan A backend (real file wins):
//  * The detail / by-tax-id endpoints return the Supplier ENTITY with its
//    branches (Vendor rows) + contacts INCLUDED. There is no `SupplierWithBranches`
//    export in `@dcc-erp/shared` (the plan named one that does not exist), so we
//    compose the response shape locally from the real shared types.
//  * The create body is `CreateSupplierWithBranch` (entity fields + nested
//    `branch`) — NOT a `{ supplier, branch }` envelope, and NOT the plan's
//    fictional `CreateSupplier` POST body.
//  * Branch create/update bodies are `CreateSupplierBranch`/`UpdateSupplierBranch`
//    — NOT the company-location master's `CreateBranch`/`UpdateBranch` (a different
//    shape; the shared barrel deliberately keeps the names distinct, see
//    packages/shared/src/schemas/supplier.ts).
export interface SupplierWithBranches extends Supplier {
  vendors: SupplierBranch[];
  contacts: SupplierContact[];
}

// companyId-scoped cache, mirroring ap.ts / vendors.ts.
const suppliersKey = (companyId: string | undefined) => ['suppliers', companyId] as const;

export interface SupplierListOpts {
  q?: string;
  active?: boolean;
}

// Aggregated list — one row per Supplier (branchCount + rdVerdict chip + active).
export function useSuppliers(companyId: string | undefined, opts: SupplierListOpts = {}) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.active !== undefined) qs.set('active', String(opts.active));
  const search = qs.toString();
  return useQuery({
    queryKey: [...suppliersKey(companyId), opts],
    queryFn: () =>
      api<SupplierListRow[]>(`/companies/${companyId}/suppliers${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// One supplier entity + its branch rows (Vendor[]).
export function useSupplier(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['suppliers', companyId, id],
    queryFn: () => api<SupplierWithBranches>(`/companies/${companyId}/suppliers/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Resolve a Supplier (+branches) by 13-digit taxId. 404 is an EXPECTED "no
// match" signal (caller reads isError to drive RD-sourced add) — retry off.
export function useSupplierByTaxId(
  companyId: string | undefined,
  taxId: string | undefined | null,
) {
  return useQuery({
    queryKey: ['suppliers', companyId, 'by-tax-id', taxId],
    queryFn: () =>
      api<SupplierWithBranches>(`/companies/${companyId}/suppliers/by-tax-id/${taxId}`),
    enabled: !!companyId && !!taxId,
    retry: false,
  });
}

// The create endpoint (SuppliersService.createWithBranch) returns the
// `{ supplier, branch }` envelope — NOT a `SupplierWithBranches` entity. There
// is no top-level `id` on this response; callers must read `created.supplier.id`.
export interface CreateSupplierResult {
  supplier: Supplier;
  branch: SupplierBranch;
}

export function useCreateSupplier(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSupplierWithBranch) =>
      apiJson<CreateSupplierWithBranch, CreateSupplierResult>(
        'POST',
        `/companies/${companyId}/suppliers`,
        data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: suppliersKey(companyId) }),
  });
}

export function useAddBranch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateSupplierBranch }) =>
      apiJson<CreateSupplierBranch, unknown>(
        'POST',
        `/companies/${companyId}/suppliers/${id}/branches`,
        data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: suppliersKey(companyId) }),
  });
}

export function useUpdateSupplier(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplier }) =>
      apiJson<UpdateSupplier, SupplierWithBranches>(
        'PATCH',
        `/companies/${companyId}/suppliers/${id}`,
        data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: suppliersKey(companyId) }),
  });
}

export function useUpdateBranch(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      vendorId,
      data,
    }: {
      id: string;
      vendorId: string;
      data: UpdateSupplierBranch;
    }) =>
      apiJson<UpdateSupplierBranch, unknown>(
        'PATCH',
        `/companies/${companyId}/suppliers/${id}/branches/${vendorId}`,
        data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: suppliersKey(companyId) }),
  });
}

// ── M2-d feature hooks ──────────────────────────────────────────────────────
// Supplier-360 read aggregate (open AP + aging + last-paid + YTD + recent docs).
export function useSupplier360(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['suppliers', companyId, id, 'overview'],
    queryFn: () => api<Supplier360>(`/companies/${companyId}/suppliers/${id}/overview`),
    enabled: !!companyId && !!id,
  });
}

// Bulk RD re-verify selected suppliers (single-flight loop server-side).
export function useBulkReverify(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierIds: string[]) =>
      apiJson<{ supplierIds: string[] }, BulkReverifyResult>(
        'POST',
        `/companies/${companyId}/suppliers/bulk-reverify`,
        { supplierIds },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: suppliersKey(companyId) }),
  });
}

// Contacts sub-table (chasing original documents).
const contactsKey = (companyId: string | undefined, id: string | undefined) =>
  ['suppliers', companyId, id, 'contacts'] as const;

export function useSupplierContacts(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: contactsKey(companyId, id),
    queryFn: () => api<SupplierContact[]>(`/companies/${companyId}/suppliers/${id}/contacts`),
    enabled: !!companyId && !!id,
  });
}

// Upsert: body carrying an `id` updates an existing contact, absent ⇒ create
// (mirrors the controller's UpsertSupplierContactSchema = create + optional id).
export function useUpsertContact(companyId: string | undefined, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateSupplierContact & { id?: string }) =>
      apiJson<CreateSupplierContact & { id?: string }, SupplierContact>(
        'POST',
        `/companies/${companyId}/suppliers/${id}/contacts`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(companyId, id) }),
  });
}

export function useDeleteContact(companyId: string | undefined, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      apiJson<undefined, void>(
        'DELETE',
        `/companies/${companyId}/suppliers/${id}/contacts/${contactId}`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(companyId, id) }),
  });
}

// Append-only supplier master change history (change-history drawer).
export function useSupplierChangeLog(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['suppliers', companyId, id, 'changelog'],
    queryFn: () => api<SupplierChangeLogRow[]>(`/companies/${companyId}/suppliers/${id}/changelog`),
    enabled: !!companyId && !!id,
  });
}

// ── Per-line memorized coding (§15.1) ───────────────────────────────────────
// RECONCILED with the real Plan C1.7 backend (real file wins): the shipped
// endpoint is `GET /companies/:c/doc-ingest/:ingestId/line-coding?supplierId&q`
// — it takes the line DESCRIPTION (`q`) and returns the SINGLE learned coding
// suggestion `{ expenseCode, departmentId } | null` (server-side normalize +
// lookup of `VendorLineCoding`). There is NO `/suppliers/:id/line-coding` array
// endpoint (the plan's C2.7 code targeted one that does not exist), so this hook
// reads the real single-suggestion route per line description. The pure
// `suggestForLine`/`normalizeDescription` helper (components/doc-ingest/
// memorized-coding.ts) mirrors that same normalization for client-side use.
export type LineCodingSuggestion = { expenseCode: string; departmentId: string | null } | null;

export function useMemorizedCoding(
  companyId: string | undefined,
  ingestId: string | undefined,
  supplierId: string | undefined,
  description: string | undefined,
) {
  const q = (description ?? '').trim();
  return useQuery({
    queryKey: ['doc-ingest', companyId, ingestId, 'line-coding', supplierId, q],
    queryFn: () =>
      api<LineCodingSuggestion>(
        `/companies/${companyId}/doc-ingest/${ingestId}/line-coding?supplierId=${supplierId}&q=${encodeURIComponent(q)}`,
      ),
    enabled: !!companyId && !!ingestId && !!supplierId && q.length > 0,
  });
}

// ── M7 Phase 2 — vendor bank-change 4-eyes (VBC) ────────────────────────────
// A bank-account change to a Vendor is NEVER a direct PATCH (D2). It is a 4-eyes
// VendorBankChangeRequest (docType 'VBC', DECISION-Z): the maker requests + submits,
// an approver records the out-of-band attestation (D3) then approves, and ONLY on
// final approval the Vendor bank columns are written (D1). So every mutation
// invalidates BOTH the VBC list key AND the suppliers cache (the Vendor's masked
// bank refreshes after final approval).
const vbcKey = (companyId: string | undefined, vendorId: string | undefined) =>
  ['vendor-bank-change', companyId, vendorId] as const;

function invalidateVbc(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  vendorId: string,
) {
  void qc.invalidateQueries({ queryKey: vbcKey(companyId, vendorId) });
  void qc.invalidateQueries({ queryKey: suppliersKey(companyId) });
}

export function useVendorBankChanges(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  return useQuery({
    queryKey: vbcKey(companyId, vendorId),
    queryFn: () =>
      api<VendorBankChange[]>(
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change`,
      ),
    enabled: !!companyId && !!supplierId && !!vendorId,
  });
}

export function useCreateVendorBankChange(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVendorBankChange) =>
      apiJson<CreateVendorBankChange, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change`,
        data,
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}

export function useConfirmVendorBankChange(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reqId, data }: { reqId: string; data: ConfirmVendorBankChange }) =>
      apiJson<ConfirmVendorBankChange, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change/${reqId}/confirm`,
        data,
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}

export function useSubmitVendorBankChange(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reqId: string) =>
      apiJson<undefined, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change/${reqId}/submit`,
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}

export function useApproveVendorBankChangeStep(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reqId,
      step,
      comment,
      sodOverrideReason,
    }: { reqId: string } & ApproveVendorBankChange) =>
      apiJson<ApproveVendorBankChange, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change/${reqId}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}

export function useRejectVendorBankChange(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reqId,
      step,
      reason,
      sodOverrideReason,
    }: { reqId: string } & RejectVendorBankChange) =>
      apiJson<RejectVendorBankChange, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change/${reqId}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}

export function useReturnVendorBankChange(
  companyId: string | undefined,
  supplierId: string | undefined,
  vendorId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reqId,
      step,
      reason,
      sodOverrideReason,
    }: { reqId: string } & ReturnVendorBankChange) =>
      apiJson<ReturnVendorBankChange, VendorBankChange>(
        'POST',
        `/companies/${companyId}/suppliers/${supplierId}/branches/${vendorId}/bank-change/${reqId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateVbc(qc, companyId, vendorId ?? ''),
  });
}
